<template>
  <div class="w-100">
    <v-skeleton-loader v-if="!stripePaymentElementMounted" type="article, article" />
    <div ref="paymentElement" />
    <v-messages
      v-show="errorBucket.length > 0"
      :value="errorBucket"
      class="mb-2 pl-3"
      color="error"
    />
  </div>
</template>

<script>
import Validatable from 'vuetify/lib/mixins/validatable'

export default {
  name: 'StripePaymentElement',
  mixins: [Validatable],
  props: {
    required: {
      type: Boolean,
      default: false
    },
    validationMessages: {
      type: Object,
      default: () => {}
    },
    locale: {
      type: String,
      default: 'en_US'
    },
    clientSecret: {
      type: String,
      required: true
    },
    stripePublishableKey: {
      type: String,
      required: true
    }
  },
  data: () => ({
    scriptNode: null,
    stripePaymentElement: null,
    stripePaymentElementMounted: false,
    stripePaymentElementLatestEvent: null,
    stripePaymentElementHasEmitted: false
  }),
  computed: {
    hasErrors() {
      return this.errorBucket.length > 0
    },
    stripeElements() {
      // stripeElements should change every time locale, clientSecret, or stripeAccount changes.
      // Therefore stripeElements should be a computed property.
      const { locale, clientSecret, stripePublishableKey } = this

      // https://stripe.com/docs/payments/accept-a-payment?platform=web&ui=elements#set-up-stripe-elements
      return this.initStripeElements(locale, clientSecret, stripePublishableKey)
    }
  },
  mounted() {
    this.stripePaymentElement = this.stripeElements.create('payment', {
      wallets: {
        applePay: 'auto', // Set to auto to enable Apple Pay
        googlePay: 'auto' // Set to auto to enable Apple Pay
      },
      fields: {
        billingDetails: {
          name: 'never',
          email: 'never',
          address: {
            country: 'auto'
          }
        }
      }
    })
    this.stripePaymentElement.mount(this.$refs.paymentElement)
    this.stripePaymentElementMounted = true
    this.stripePaymentElement.on('change', this.handlePaymentElementChange)
    this.stripePaymentElement.on('blur', this.handlePaymentElementBlur)
    this.stripePaymentElement.on('escape', this.handlePaymentElementBlur)
    this.stripePaymentElement.on('focus', this.handlePaymentElementFocus)
    this.$emit('payment-element', this.stripePaymentElement)
  },
  destroyed() {
    // console.debug('stripePaymentElement destroyed')
    this.$emit('payment-element', null)
  },
  methods: {
    initStripe(stripePublishableKey) {
      // const { stripeAccount } = this

      // const stripeOptions = {}
      // if (stripeAccount) {
      //   stripeOptions.stripeAccount = stripeAccount
      // }
      // const stripe = window.Stripe(this.stripePublishableKey, stripeOptions)
      const stripe = window.Stripe(stripePublishableKey, {})
      this.$emit('stripe', stripe)

      return stripe
    },
    initStripeElements(locale, clientSecret, stripePublishableKey) {
      const stripe = this.initStripe(stripePublishableKey)

      // https://stripe.com/docs/js/elements_object/create
      const elements = stripe.elements({
        locale,
        clientSecret,
        appearance: {
          theme: 'stripe',
          // https://stripe.com/docs/stripe-js/appearance-api#variables
          variables: {
            fontFamily: '"proxima-nova", sans-serif',
            colorDanger: '#ff5252',
            fontSizeSm: '12px'
          }
        },
        // https://stripe.com/docs/stripe-js/appearance-api#rules
        rules: {
          '.Error': {
            fontSize: '12px'
          },
          '.p-FieldError': {
            paddingLeft: '12px'
          },
          '.TermsText': {
            letterSpacing: '0.0333333333em'
          }
        }
      })

      this.$emit('stripe-elements', elements)

      return elements
    },
    applyErrorClassIfErrors() {
      if (this.hasErrors) {
        this.$refs.paymentElement.classList.add('StripeElement--invalid')
      }
    },
    validateAndApplyStyles(event) {
      this.validate(true, event)
      this.applyErrorClassIfErrors()
    },
    handlePaymentElementChange(event) {
      this.stripePaymentElementHasEmitted = true
      // https://stripe.com/docs/js/element/events/on_change?type=paymentElement
      // Pass the event object to validate. That way, we can expose the object to validation rules
      this.stripePaymentElementLatestEvent = event
      this.validateAndApplyStyles(event)
      this.$emit('change', event)
    },
    handlePaymentElementBlur() {
      this.stripePaymentElementHasEmitted = true
      if (this.stripePaymentElementLatestEvent) {
        const { empty } = this.stripePaymentElementLatestEvent
        if (empty) {
          // If the field is completely empty, reset the required check
          this.stripePaymentElementLatestEvent = null
        }
      }
      this.validateAndApplyStyles(this.stripePaymentElementLatestEvent)
      this.$emit('blur', this.stripePaymentElementLatestEvent)
    },
    handlePaymentElementFocus() {
      this.validateAndApplyStyles(this.stripePaymentElementLatestEvent)
    },
    validate(force = false, value) {
      // Adapted from https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/mixins/validatable/index.ts
      const errorBucket = []
      value = value || this.internalValue

      // Add a field required check if the field has been touched already
      if (
        this.required &&
        this.stripePaymentElementHasEmitted &&
        this.stripePaymentElementLatestEvent === null
      ) {
        // this only happens if we have a pristine, untouched field that is required so enqueue the
        // required error message
        const { required } = this.validationMessages
        if (required) {
          errorBucket.push(required)
        } else {
          throw new Error(
            'Required message not defined. Please add a "required" property to validationMessages object'
          )
        }
      }

      const { error, empty, complete } = this.stripePaymentElementLatestEvent || {}

      if (error) {
        const { message } = error
        errorBucket.push(message)
      } else {
        if (force) this.hasInput = this.hasFocused = true

        for (let index = 0; index < this.rules.length; index++) {
          const rule = this.rules[index]
          const valid = typeof rule === 'function' ? rule(value) : rule

          if (valid === false || typeof valid === 'string') {
            errorBucket.push(valid || '')
          } else if (typeof valid !== 'boolean') {
            // eslint-disable-next-line no-console
            console.error(
              `Rules should return a string or boolean, received '${typeof valid}' instead`
            )
          }
        }
      }

      if (errorBucket.length === 0 && empty === false && !complete) {
        // Fire the incomplete error message if the card ield has content, is incomplete and there
        // are no other errors
        const { incomplete } = this.validationMessages
        if (incomplete) {
          errorBucket.push(incomplete)
        } else {
          throw new Error(
            'Incomplete message not defined. Please add an "incomplete" property to validationMessages object'
          )
        }
      }

      this.errorBucket = errorBucket
      this.valid = errorBucket.length === 0

      return this.valid
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_allout-vars.scss';

.StripeElement {
  .Error {
    padding-left: 12px;
  }
}
</style>
